import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { Edit01Icon, Trash01Icon } from '@langchain/untitled-ui-icons';
import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { ReactNode, useMemo, useState } from 'react';

import { useStateFromSearchParams } from '@/hooks/useStateFromSearchParams';
import { AlertRuleWithActions } from '@/types/alerts';
import { getAlertsPath } from '@/utils/constants';

import { AlertsViewPane } from './Alerts/AlertsPane';
import { DataGrid } from './DataGrid';
import { DeleteModal } from './Delete';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './Dropdown';
import { TableCellTextOverflow } from './Table';
import { TextOverflowTooltip } from './TextOverflowTooltip';

interface AlertsDataGridProps {
  alerts: AlertRuleWithActions[];
  sessionId: string;
  onEditClick: (alert: AlertRuleWithActions) => void;
  onDeleteSuccess: () => void;
  isLoading?: boolean;
  emptyState?: ReactNode | null;
}

const columnHelper = createColumnHelper<AlertRuleWithActions>();

const COLUMNS_DEF: ColumnDef<AlertRuleWithActions, any>[] = [
  columnHelper.accessor('rule.name', {
    id: 'name',
    header: 'Name',
    size: 100,
    cell: ({ row }) => (
      <TextOverflowTooltip tooltipMaxWidth={300}>
        {row.original.rule.name}
      </TextOverflowTooltip>
    ),
  }),
  columnHelper.accessor('rule.description', {
    id: 'description',
    header: 'Alert Trigger',
    size: 200,
    cell: ({ row }) => (
      <TextOverflowTooltip className="text-sm text-tertiary no-scrollbar">
        {row.original.rule.description}
      </TextOverflowTooltip>
    ),
  }),
  columnHelper.accessor('actions', {
    id: 'notification_type',
    header: 'Notification Type',
    cell: ({ row }) => {
      return (
        <div className="flex gap-1">
          {row.original.actions.map((action) => (
            <div
              className="rounded-md border border-secondary bg-secondary px-1 py-0.5 text-xs text-primary"
              key={action.id}
            >
              {action.target}
            </div>
          ))}
        </div>
      );
    },
    size: 40,
  }),
  columnHelper.display({
    id: 'actions',
    cell: ({ row, table }) => {
      const { actions } = (table.options.meta ?? {}) as {
        actions: {
          deleteEnabled: boolean;
          editEnabled: boolean;
          handleDelete: (alert: AlertRuleWithActions) => void;
          handleEdit: (alert: AlertRuleWithActions) => void;
        };
      };

      if (!actions?.deleteEnabled && !actions?.editEnabled) return null;

      return (
        <TableCellTextOverflow className="justify-center">
          <DropdownMenu>
            <DropdownMenuTrigger
              onClick={(e) => e.stopPropagation()}
              className="flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:bg-secondary focus:outline-none"
              data-testid="dropdown-menu-trigger"
            >
              <EllipsisVerticalIcon className="h-5 w-5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="z-10"
              onClick={(e) => e.stopPropagation()}
            >
              {actions?.editEnabled && (
                <DropdownMenuItem
                  onClick={() => actions?.handleEdit?.(row.original)}
                  className="flex items-center gap-2"
                >
                  <Edit01Icon className="h-5 w-5" />
                  Edit
                </DropdownMenuItem>
              )}
              {actions?.deleteEnabled && (
                <DropdownMenuItem
                  onClick={() => actions?.handleDelete?.(row.original)}
                  className="flex items-center gap-2"
                >
                  <Trash01Icon className="h-5 w-5" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCellTextOverflow>
      );
    },
    size: 40,
  }),
];

export const AlertsDataGrid = ({
  alerts,
  sessionId,
  onEditClick,
  onDeleteSuccess,
  isLoading,
  emptyState,
}: AlertsDataGridProps) => {
  'use no memo';
  const [alertToDelete, setAlertToDelete] =
    useState<AlertRuleWithActions | null>(null);

  const handleDeleteClick = (alert: AlertRuleWithActions) => {
    setAlertToDelete(alert);
  };

  const handleCancelDelete = () => {
    setAlertToDelete(null);
  };

  const [peekedAlertId, setPeekedAlertId] =
    useStateFromSearchParams('peekAlert');

  const alertToView = useMemo(() => {
    return alerts.find((a) => a.rule.id === peekedAlertId);
  }, [peekedAlertId, alerts]);

  const onRowClick = (row: AlertRuleWithActions) => {
    if (row.rule.id) {
      setPeekedAlertId(row.rule.id);
    }
  };

  const table = useReactTable({
    data: alerts,
    columns: COLUMNS_DEF,
    meta: {
      actions: {
        handleDelete: handleDeleteClick,
        handleEdit: onEditClick,
        deleteEnabled: true,
        editEnabled: true,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),

    manualExpanding: true,
    manualFiltering: true,
    manualPagination: true,
    enableSorting: false,
    enableHiding: false,
    enableColumnResizing: false,
  });

  return (
    <>
      <DataGrid
        onClick={onRowClick}
        table={table}
        cellSpacingVariant="wide"
        isLoading={!!isLoading}
        stickyRightColumn
        emptyState={emptyState}
      />

      <DeleteModal
        endpoint={`${getAlertsPath()}/${sessionId}`}
        id={alertToDelete?.rule.id}
        name={alertToDelete?.rule.name}
        isOpen={!!alertToDelete}
        doClose={handleCancelDelete}
        onSuccess={onDeleteSuccess}
      />
      <AlertsViewPane
        isOpen={!!alertToView}
        alert={alertToView}
        onClose={() => setPeekedAlertId(null)}
        sessionId={sessionId}
        onEditClick={onEditClick}
      />
    </>
  );
};
